import { MaskLine, Rise } from "@/components/motion/Reveal";
import { HERITAGE, OFFICES } from "@/data/site";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldLegalService, ldBreadcrumb } from "@/seo/seoConfig";

const OFFICE_IMG = "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&w=2400&q=85";

export default function AboutPage() {
  const { t } = useLang();
  return (
    <div className="pt-40">
      <Seo
        title="About the Chambers · Law × Technology × Regulation × Business"
        description="RPLC UAE — a Dubai-focused legal & business consultancy operating across UAE, India and USA. Meet the founding partners, the firm's practice areas, and the RAKEZ-registered entity Rane Pingle and Partners FZ LLC."
        path="/about"
        keywords={["law firm Dubai", "law firm UAE", "RAKEZ law firm", "law firm Pune", "cross-border lawyer UAE India USA", "Rane Pingle Law Chambers", "RPLC UAE"]}
        jsonLd={ldBreadcrumb([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 overflow-x-hidden">
        <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{t.about.eyebrow}</span></div></Rise>
        <h1 className="font-serif tracking-tight leading-[0.95] text-[9vw] sm:text-[8.5vw] lg:text-[7.5vw] break-words">
          <MaskLine delay={0.15}>{t.about.title1}</MaskLine>
          <MaskLine delay={0.32}><span className="italic-accent">{t.about.title2}</span></MaskLine>
        </h1>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 mt-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <Rise className="lg:col-span-6" y={40}>
          <p className="font-serif text-3xl lg:text-4xl leading-tight text-[#F4F1EB]">{t.about.lead1}</p>
          <p className="mt-8 text-[#A19D94] text-lg leading-relaxed">{t.about.lead2}</p>
          <p className="mt-6 text-[#A19D94] text-lg leading-relaxed">{t.about.lead3}</p>
        </Rise>
        <div className="lg:col-span-6">
          <Rise>
            <div className="relative overflow-hidden border border-[#141414]">
              <img src={OFFICE_IMG} alt="Chambers interior" className="w-full h-[600px] object-cover photo-mono" />
              <div className="absolute top-4 left-4 chapter-num text-[#F4F1EB]">Fig 01 · Chambers</div>
            </div>
          </Rise>
        </div>
      </section>

      {/* NUMBERS */}
      <section className="mt-40 border-y border-[#141414] py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {t.about.stats.map((s, i) => (
            <Rise key={s.l} delay={i * 0.08}>
              <div className="border-l border-[#222] pl-6">
                <div className="font-serif text-6xl lg:text-7xl text-[#F4F1EB] leading-none">{s.n}</div>
                <div className="mt-4 eyebrow">{s.l}</div>
              </div>
            </Rise>
          ))}
        </div>
      </section>

      {/* HERITAGE FULL */}
      <section className="py-32 lg:py-48">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-4">
              <Rise><div className="flex items-center gap-4 mb-8"><span className="accent-bar" /><span className="eyebrow">{t.heritage.eyebrow}</span></div></Rise>
              <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">{t.heritage.title1} <span className="italic-accent">{t.heritage.title2}</span></h2></Rise>
            </div>
            <div className="lg:col-span-8 space-y-16">
              {HERITAGE.map((h, i) => (
                <Rise key={h.year} delay={i * 0.06}>
                  <div className="grid grid-cols-12 gap-6 pb-16 border-b border-[#141414]">
                    <div className="col-span-4 lg:col-span-3">
                      <div className="font-serif text-3xl lg:text-4xl text-[#C84C35] leading-tight">{h.year}</div>
                    </div>
                    <div className="col-span-8 lg:col-span-9">
                      <div className="font-serif text-2xl lg:text-3xl">{h.title}</div>
                      <p className="mt-3 text-[#A19D94] leading-relaxed max-w-xl">{h.body}</p>
                    </div>
                  </div>
                </Rise>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* OFFICES */}
      <section className="py-24 border-y border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{t.about.offices.eyebrow}</span></div></Rise>
          <Rise delay={0.1}><h2 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-16">{t.about.offices.title1} <span className="italic-accent">{t.about.offices.title2}</span></h2></Rise>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#141414] border border-[#141414]">
            {OFFICES.map((o, i) => (
              <Rise key={o.city} delay={i * 0.06}>
                <div className="bg-[#050505] p-8 lg:p-10 h-full min-h-[280px] flex flex-col justify-between">
                  <div>
                    <div className="chapter-num">{o.role}</div>
                    <div className="font-serif text-4xl lg:text-5xl mt-4">{o.city}</div>
                    <div className="italic-accent text-[#A19D94] mt-1">{o.country}</div>
                  </div>
                  <div className="mt-8 text-sm text-[#A19D94] leading-relaxed">
                    <div>{o.address}</div>
                    <div className="chapter-num mt-3">{o.coords}</div>
                  </div>
                </div>
              </Rise>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
